import React, { useCallback, useState } from 'react';
import { User, Team, Tag } from '../types/user';
import UserTableRow from './UserTableRow';
import { UserRole } from '../../settings/UserRoles';
import { useAdmin } from '../../../providers/AdminProvider';
import { Spinner } from '../../../utils/Spinner';
import { Invitation } from '../../../serverActions/admin';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import VetRecButton from '../../common/VetRecButton';

interface UserTableProps {
  users: User[];
  selectedUsers: string[];
  selectAll: boolean;
  teams: Team[];
  tags: Tag[];
  onSelectAll: () => void;
  onSelectUser: (userId: string) => void;
  onResendInvite: (invitations:Invitation[]) => Promise<void>;
  onRevokeInvite: (invitations:Invitation[]) => Promise<void>;
  onRemoveUser: (userId: string[]) => Promise<void>;
  saveAllChanges: () => Promise<void>;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  selectedUsers,
  selectAll,
  teams,
  tags,
  onSelectAll,
  onSelectUser,
  onResendInvite,
  onRevokeInvite,
  onRemoveUser,
  saveAllChanges,
}) => {
  const { loading, pendingUserChanges, handleUserChanges, clearUserChanges } = useAdmin();

  const [sortDirection, setSortDirection] = useState<['user' | 'role' 
    | 'tag' | 'none','none'|'asc' | 'desc']>(['none','none']);

  const getTeamById = useCallback((teamId: string): Team | undefined => {
    return teams.find(team => team.id === teamId);
  }, [teams]);

  const getTagById = useCallback((tagId: string): Tag | undefined => {
    return tags.find(tag => tag.id === tagId);
  }, [tags]);

  const handleSort = (column:'user' | 'tag' | 'role') => {
    if (sortDirection[1] === 'none') {
      setSortDirection([column,'asc']);
    } else if (sortDirection[1] === 'asc') {
      setSortDirection([column, 'desc']);
    } else {
      setSortDirection(['none', 'none']);
    }
  }
  
  const sortedUsers = [...users].sort((a, b) => {
    if (sortDirection[0] === 'user') {
      return sortDirection[1] === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    } else if (sortDirection[0] === 'role') {
      return sortDirection[1] === 'asc' ? a.role.localeCompare(b.role) : b.role.localeCompare(a.role);
    } else if (sortDirection[0] === 'tag') {
      const tagA = tags.find(tag => tag.id === a.tags[0]);
      const tagB = tags.find(tag => tag.id === b.tags[0]);
      return sortDirection[1] === 'asc' ? (tagA?.name || '').localeCompare(tagB?.name || '') : (tagB?.name || '').localeCompare(tagA?.name || '');
    }
    return 0;
  });

  return (
    <div className="mt-6 flex flex-col">
      <div className="overflow-visible">
        <div className="inline-block min-w-full max-w-full py-2 align-middle">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm sm:text-lg font-semibold">User Management</span>
            <div className='flex flex-row gap-x-2'>
              <VetRecButton
                className={`inline-flex items-center px-1 sm:px-4 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium bg-blue-600 border border-gray-300 text-white ${Object.keys(pendingUserChanges).length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-500'}`}
                onClick={saveAllChanges}
                disabled={Object.keys(pendingUserChanges).length === 0}
              >
                Save Changes
              </VetRecButton>
              <VetRecButton
                className={`inline-flex items-center px-1 sm:px-4 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium border border-gray-300 text-gray-700 bg-white ${Object.keys(pendingUserChanges).length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                onClick={clearUserChanges}
                disabled={Object.keys(pendingUserChanges).length === 0}
              >
                Clear Changes
              </VetRecButton>
            </div>
          </div>
          <div className={`overflow-visible border border-gray-300 ring-opacity-5 rounded-md max-w-full overflow-y-auto ${selectedUsers.length > 0 ? "h-[37vh] lg:h-[50vh]" : "h-[52vh] lg:h-[60vh]"} thin-scrollbar thin-scrollbar-x`}>
            <table className="min-w-full max-w-full divide-y divide-gray-600 rounded-md">
              <thead className="bg-gray-50 sticky top-0 z-10 border-b-1 border-gray-300 shadow-md">
                <tr>
                  <th scope="col" className="relative py-3.5 pl-4 pr-3 text-left text-xs sm:text-sm font-semibold text-gray-900">
                    <input
                      type="checkbox"
                      className="absolute h-3 sm:h-4 w-3 sm:w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 left-4 top-1/2 -mt-2"
                      checked={selectAll}
                      onChange={onSelectAll}
                    />
                    <div className='flex flex-row items-center gap-x-4'>
                      <span className="ml-7">User</span>
                      {sortComponent('user')}
                    </div>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900">
                    <div className='flex flex-row items-center gap-x-4'>
                      <span>Role</span>
                      {sortComponent('role')}
                    </div>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900">Teams</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900">
                    <div className='flex flex-row gap-x-4 items-center'>
                      <span>Tag</span>
                      {sortComponent('tag')}
                    </div>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedUsers.length > 0 && !loading ? (
                  sortedUsers.map((user) => (
                    <UserTableRow
                      key={user.id}
                      user={user}
                      teams={teams}
                      tags={tags}
                      isSelected={selectedUsers.includes(user.id)}
                      onSelectUser={onSelectUser}
                      getTeamById={getTeamById}
                      getTagById={getTagById}
                      onResendInvite={onResendInvite}
                      onRevokeInvite={onRevokeInvite}
                      removeUser={onRemoveUser}
                      onRowChange={handleUserChanges}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">
                      <div className="flex flex-col items-center gap-y-2 w-full">
                        {loading && <Spinner timer={false} size='h-5 w-5'/>}
                        {loading ? "Loading users..." :"No users found matching your filters"}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  function sortComponent(column: 'user' | 'role' | 'tag' = 'user') {
    return <div className='flex flex-col cursor-pointer' onClick={() => { handleSort(column); } }>
      <ChevronUpIcon className={`h-2 sm:h-3 w-2 sm:w-3 ${sortDirection[0] === column && sortDirection[1] === "asc" ? "text-gray-900" : "text-gray-500"}`} />
      <ChevronDownIcon className={`h-2 sm:h-3 w-2 sm:w-3 ${sortDirection[0] === column && sortDirection[1] === "desc" ? "text-gray-900" : "text-gray-500"}`} />
    </div>;
  }
};

export default UserTable;
